import { Button, Container, Grid, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import countryList from 'react-select-country-list';
import styled from 'styled-components';
import * as yup from 'yup';
import { route } from '../route/Index';
import InputText from '../shared/component/InputText';
import { IProjectInfo } from '../shared/iProject';
import { ENodeEnv } from '../store/enum';
import { useAppDispatch, useAppSelector } from '../store/hook';
import { selectProject, setProjectInfo } from '../store/project/projectSlice';
import { selectUser } from '../store/user/userSlice';
import Map from './map/Map';
import DropDown from '../shared/component/DropDown';
import { MenuItem } from '@mui/material';

interface IInfoProps {}

const InfoContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
    margin-top: 1rem;
`;

type TProjectInfo = keyof IProjectInfo;

export const title: TProjectInfo = 'title';
const projectNumber: TProjectInfo = 'projectNumber';
const client: TProjectInfo = 'client';
const companyName: TProjectInfo = 'companyName';
const country: TProjectInfo = 'country';
const city: TProjectInfo = 'city';
const latitude: TProjectInfo = 'latitude';
const longitude: TProjectInfo = 'longitude';
const notes: TProjectInfo = 'notes';

const Info = (props: IInfoProps) => {
    const dispatch = useAppDispatch();
    const project = useAppSelector(selectProject);
    const user = useAppSelector(selectUser);
    const { t } = useTranslation();

    const options = React.useMemo(() => countryList().getData(), []);

    const navigate = useNavigate();

    let initialValues = {
        authorEmail: user.data!.email,
        authorName: user.data!.first_Name,
        authorSurname: user.data!.last_Name,
        title: project?.data?.project?.title ? project?.data?.project?.title : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
        projectNumber: project?.data?.project?.projectNumber ? project?.data?.project?.projectNumber : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
        client: project?.data?.project?.client ? project?.data?.project?.client : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
        companyName: project?.data?.project?.companyName ? project?.data?.project?.companyName : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
        country: project?.data?.project?.country ? project?.data?.project?.country : options.find(x => x.value == 'IT')!.label,
        city: project?.data?.project?.city ? project?.data?.project?.city : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
        latitude: project?.data?.project?.latitude,
        longitude: project?.data?.project?.longitude,
        notes: project?.data?.project?.notes ? project?.data?.project?.notes : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
    };

    const formik = useFormik<IProjectInfo>({
        initialValues,
        validationSchema: yup.object({
            title: yup.string().required('Required'),
            client: yup.string().required('Required'),
            companyName: yup.string().required('Required'),
            country: yup.string().required('Required'),
            city: yup.string().required('Required'),
            latitude: yup.number().required('Required'),
            longitude: yup.number().required('Required'),
        }),
        onSubmit: values => {
            dispatch(setProjectInfo(values));
            navigate(route.geometry);
        },
    });

    const handleChangeProjectInfo = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        if (name == latitude || name == longitude) value = parseFloat(value);
        let updateValue = { [name]: value };
        formik.setValues({ ...formik.values, ...updateValue });
    };

    return (
        <InfoContainer maxWidth='md'>
            <Stack height={'100%'} direction='column' spacing={4}>
                <form onSubmit={formik.handleSubmit}>
                    <Stack flexGrow={1}>
                        <Grid container justifyContent='center' spacing={2}>
                            <Grid item md={6}>
                                <Stack direction={'column'}>
                                    <Typography variant='h2' gutterBottom>
                                        {t('projectDetails')}
                                    </Typography>
                                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1}>
                                        <InputText required customValue={formik.values.title} id={title} name={title} inputLabel={t(title)} customOnChange={handleChangeProjectInfo} />
                                        <InputText
                                            isNumber
                                            customValue={formik.values.projectNumber}
                                            id={projectNumber}
                                            inputLabel={t(projectNumber)}
                                            name={projectNumber}
                                            customOnChange={handleChangeProjectInfo}
                                        />
                                    </Stack>
                                    <InputText required customValue={formik.values.client} id={client} inputLabel={t(client)} name={client} customOnChange={handleChangeProjectInfo} />
                                    <Typography style={{ paddingTop: '1rem' }} variant='h2' gutterBottom>
                                        {t('companyDetails')}
                                    </Typography>
                                    <InputText
                                        required
                                        customValue={formik.values.companyName}
                                        id={companyName}
                                        inputLabel={t(companyName)}
                                        name={companyName}
                                        customOnChange={handleChangeProjectInfo}
                                    />
                                    <DropDown
                                        required
                                        inputLabel={t(country)}
                                        labelId={`${country}-label`}
                                        id={country}
                                        name={country}
                                        value={formik.values.country}
                                        onChange={e => handleChangeProjectInfo(e)}>
                                        {options.map(x => (
                                            <MenuItem value={x.label}>{x.label}</MenuItem>
                                        ))}
                                    </DropDown>
                                    <InputText required customValue={formik.values.city} id={city} inputLabel={t(city)} name={city} customOnChange={handleChangeProjectInfo} />
                                    <Typography variant='subtitle1' gutterBottom>
                                        {t('requiredFiled')}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item md={6}>
                                <Stack direction={'column'}>
                                    <Typography variant='h2' gutterBottom>
                                        {t('location')}
                                    </Typography>
                                    <Stack direction={'row'} justifyContent={'space-between'} gap={1}>
                                        <InputText
                                            isNumber
                                            required
                                            customValue={formik.values.latitude}
                                            name={latitude}
                                            id={latitude}
                                            type='number'
                                            inputLabel={t(latitude)}
                                            customOnChange={handleChangeProjectInfo}
                                        />
                                        <InputText
                                            isNumber
                                            required
                                            customValue={formik.values.longitude}
                                            name={longitude}
                                            id={longitude}
                                            type='number'
                                            inputLabel={t(longitude)}
                                            customOnChange={handleChangeProjectInfo}
                                        />
                                    </Stack>
                                    <Typography variant='subtitle1'>{t('clickOnTheMapToSetThePin')}</Typography>
                                    <Map
                                        coordinates={{ latitude: formik.values.latitude, longitude: formik.values.longitude }}
                                        onClick={coordinate => {
                                            formik.setFieldValue(latitude, coordinate.latitude);
                                            formik.setFieldValue(longitude, coordinate.longitude);
                                        }}
                                    />
                                    <InputText id={notes} name={notes} customValue={formik.values.notes} inputLabel={t(notes)} customOnChange={handleChangeProjectInfo} />
                                </Stack>
                            </Grid>
                        </Grid>
                    </Stack>
                    <Stack alignItems='end'>
                        <Button variant='orange' type='submit'>
                            {t('next')}
                        </Button>
                    </Stack>
                </form>
            </Stack>
        </InfoContainer>
    );
};

export default Info;
